import { makeEnvelope } from './envelope';
import { normalizeCoords } from './coords';
import type { Send, WireMouse, WireWheel } from './types';

const LONG_PRESS_MS = 500;
const TAP_SLOP_PX = 10;

interface TrackedFinger {
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  longPressTimer: number | null;
  moved: boolean;
  dragging: boolean;
}

// attachTouch 把触屏事件合成为鼠标 / 滚轮 wire event。
//
// 语义:
//   - 单指短按 (< 500ms 且位移 < 10px) → 左键 click(按下+释放)
//   - 单指长按(>= 500ms,没有位移)→ 右键 click
//   - 单指拖动(位移 > 10px)→ 左键 drag(mousePressed + 多个 mouseMoved + mouseReleased)
//   - 双指移动 → 滚轮(delta 取两指平均位移取反,与页面滚动方向一致)
//
// 不做多指手势(pinch 缩放、3 指手势):首发 Non-Goal,见 docs/PLAN.md。
export function attachTouch(target: HTMLElement, send: Send): () => void {
  const tracked = new Map<number, TrackedFinger>();

  const longPressFire = (f: TrackedFinger) => {
    f.longPressTimer = null;
    if (f.moved) return;
    const { x, y } = normalizeCoords(target, f.startX, f.startY);
    send(
      makeEnvelope<WireMouse>('input.mouse', {
        type: 'mousePressed',
        x,
        y,
        button: 'right',
        click_count: 1,
        modifiers: {},
      }),
    );
    send(
      makeEnvelope<WireMouse>('input.mouse', {
        type: 'mouseReleased',
        x,
        y,
        button: 'right',
        click_count: 1,
        modifiers: {},
      }),
    );
  };

  const cancelAllLongPress = () => {
    for (const f of tracked.values()) {
      if (f.longPressTimer !== null) {
        clearTimeout(f.longPressTimer);
        f.longPressTimer = null;
      }
    }
  };

  const onStart = (e: TouchEvent) => {
    e.preventDefault();
    // 当已有 >= 1 指时,新增手指 = 进入多指模式,取消 pending long-press。
    if (e.touches.length >= 2) {
      cancelAllLongPress();
    }
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      const f: TrackedFinger = {
        startX: t.clientX,
        startY: t.clientY,
        lastX: t.clientX,
        lastY: t.clientY,
        longPressTimer: null,
        moved: false,
        dragging: false,
      };
      if (e.touches.length === 1) {
        f.longPressTimer = window.setTimeout(() => longPressFire(f), LONG_PRESS_MS);
      }
      tracked.set(t.identifier, f);
    }
  };

  const onMove = (e: TouchEvent) => {
    e.preventDefault();

    if (e.touches.length >= 2) {
      // 双指滚动:取所有对应手指位移和,取反(手指下滑 → 内容上滚)
      let sumDx = 0;
      let sumDy = 0;
      let count = 0;
      let anchorX = 0;
      let anchorY = 0;
      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        const prev = tracked.get(t.identifier);
        if (!prev) continue;
        sumDx += t.clientX - prev.lastX;
        sumDy += t.clientY - prev.lastY;
        prev.lastX = t.clientX;
        prev.lastY = t.clientY;
        anchorX += t.clientX;
        anchorY += t.clientY;
        count += 1;
      }
      if (count > 0 && (sumDx !== 0 || sumDy !== 0)) {
        const { x, y } = normalizeCoords(target, anchorX / count, anchorY / count);
        send(
          makeEnvelope<WireWheel>('input.wheel', {
            x,
            y,
            delta_x: -sumDx,
            delta_y: -sumDy,
            modifiers: {},
          }),
        );
      }
      return;
    }

    // 单指:累积位移判定 tap/drag 边界。
    const t = e.touches[0];
    if (!t) return;
    const prev = tracked.get(t.identifier);
    if (!prev) return;

    if (!prev.moved) {
      const dx = t.clientX - prev.startX;
      const dy = t.clientY - prev.startY;
      if (Math.hypot(dx, dy) > TAP_SLOP_PX) {
        prev.moved = true;
        if (prev.longPressTimer !== null) {
          clearTimeout(prev.longPressTimer);
          prev.longPressTimer = null;
        }
        // 进入 drag:发 mousePressed
        const { x, y } = normalizeCoords(target, prev.startX, prev.startY);
        send(
          makeEnvelope<WireMouse>('input.mouse', {
            type: 'mousePressed',
            x,
            y,
            button: 'left',
            click_count: 1,
            modifiers: {},
          }),
        );
        prev.dragging = true;
      }
    }
    if (prev.dragging) {
      const { x, y } = normalizeCoords(target, t.clientX, t.clientY);
      send(
        makeEnvelope<WireMouse>('input.mouse', {
          type: 'mouseMoved',
          x,
          y,
          button: 'left',
          buttons: 1,
          modifiers: {},
        }),
      );
    }
    prev.lastX = t.clientX;
    prev.lastY = t.clientY;
  };

  const onEnd = (e: TouchEvent) => {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      const prev = tracked.get(t.identifier);
      if (!prev) continue;
      if (prev.longPressTimer !== null) {
        clearTimeout(prev.longPressTimer);
        prev.longPressTimer = null;
      }

      if (prev.dragging) {
        const { x, y } = normalizeCoords(target, t.clientX, t.clientY);
        send(
          makeEnvelope<WireMouse>('input.mouse', {
            type: 'mouseReleased',
            x,
            y,
            button: 'left',
            click_count: 1,
            modifiers: {},
          }),
        );
      } else if (!prev.moved) {
        // Short tap → synthesize left click at start position.
        const { x, y } = normalizeCoords(target, prev.startX, prev.startY);
        send(
          makeEnvelope<WireMouse>('input.mouse', {
            type: 'mousePressed',
            x,
            y,
            button: 'left',
            click_count: 1,
            modifiers: {},
          }),
        );
        send(
          makeEnvelope<WireMouse>('input.mouse', {
            type: 'mouseReleased',
            x,
            y,
            button: 'left',
            click_count: 1,
            modifiers: {},
          }),
        );
      }
      tracked.delete(t.identifier);
    }
  };

  const onCancel = (e: TouchEvent) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      const prev = tracked.get(t.identifier);
      if (prev?.longPressTimer !== null && prev?.longPressTimer !== undefined) {
        clearTimeout(prev.longPressTimer);
      }
      tracked.delete(t.identifier);
    }
  };

  target.addEventListener('touchstart', onStart, { passive: false });
  target.addEventListener('touchmove', onMove, { passive: false });
  target.addEventListener('touchend', onEnd, { passive: false });
  target.addEventListener('touchcancel', onCancel, { passive: false });

  return () => {
    target.removeEventListener('touchstart', onStart);
    target.removeEventListener('touchmove', onMove);
    target.removeEventListener('touchend', onEnd);
    target.removeEventListener('touchcancel', onCancel);
    cancelAllLongPress();
    tracked.clear();
  };
}
